import { Center, Divider, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { FaStar } from '@react-icons/all-files/fa/FaStar';
import { useState } from 'react';

import { LDFlags } from '../../../../../constants/flags';
import { useFlag } from '../../../../../hooks';
import { trackConversationOpened } from '../../../../../services/analytics/marketplaceEvents';
import { ContactMessageEntry } from '../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import { LeadGuidance, hasTierLanes } from '../guidance';
import MarketplaceEmptyActive from './MarketplaceEmptyActive';
import OffersSummaryRow from './OffersSummaryRow';
import OffersWaitingSteps from './OffersWaitingSteps';
import ConversationThreadDrawer from './conversation/ConversationThreadDrawer';
import { OfferConversationTarget, getLiveTarget, getMessagesByOrg } from './conversation/utils';
import InactiveOfferCard from './offers/InactiveOfferCard';
import { OfferAnalyticsProvider } from './offers/OfferAnalyticsContext';
import OfferCard from './offers/OfferCard';
import OfferActionModals from './offers/modals/OfferActionModals';
import {
  MarketplaceOffer,
  OfferAction,
  OfferActionKind,
  OfferCardView,
  getOfferCards,
} from './offers/utils';
import TierLanes from './waiting/TierLanes';
import TrustStrip from './waiting/TrustStrip';

type MarketplaceOffersProps = {
  offersEnabled: boolean;
  offers: MarketplaceOffer[] | undefined;
  messages: ContactMessageEntry[] | undefined;
  externalId: string;
  dealId: string;
  leadId: string;
  listedAt?: string | null;
  payoffAmount: number | null;
  guidance: LeadGuidance | null;
  model?: string | null;
  hasVisibleOffers: boolean;
  payoffIsTrusted: boolean;
  lienholderSlug: string | null;
  lienholderName: string | null;
};

const RULE_STATEMENT =
  'Any buyer can accept any of your counters at any time. The moment one is accepted, your other negotiations end automatically.';

const InactiveOffersHeader = () => (
  <HStack spacing="12px" pt="6px" px="2px">
    <Divider borderColor="grayMediumBackground" />
    <Text
      fontSize="11px"
      fontWeight="700"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="grayLight"
      whiteSpace="nowrap"
    >
      Inactive offers
    </Text>
    <Divider borderColor="grayMediumBackground" />
  </HStack>
);

const MarketplaceOffers = ({
  offersEnabled,
  offers,
  messages,
  externalId,
  dealId,
  leadId,
  listedAt,
  payoffAmount,
  guidance,
  model,
  hasVisibleOffers,
  payoffIsTrusted,
  lienholderSlug,
  lienholderName,
}: MarketplaceOffersProps) => {
  const messagingEnabled = useFlag(LDFlags.MARKETPLACE_MESSAGING);
  const { activeCards, inactiveCards } = getOfferCards(offers);
  const [action, setAction] = useState<OfferAction | null>(null);
  // Only a fallback; the drawer renders from the live re-derivation (getLiveTarget) below.
  const [conversation, setConversation] = useState<OfferConversationTarget | null>(null);

  const handleDashboardAction = (kind: OfferActionKind, card: OfferCardView) =>
    setAction({ kind, card, surface: 'dashboard' });
  const handleThreadAction = (kind: OfferActionKind, card: OfferCardView) =>
    setAction({ kind, card, surface: 'thread' });
  const handleCloseAction = () => setAction(null);

  // Bumped when a driver action lands so the open thread re-anchors to the newest event.
  const [threadReanchorSignal, setThreadReanchorSignal] = useState(0);
  const handleActionSuccess = () => setThreadReanchorSignal((count) => count + 1);

  const messagesByOrg: Map<string, ContactMessageEntry[]> = messagingEnabled
    ? getMessagesByOrg(messages)
    : new Map();
  // Fired here (the open action), not in the drawer, so it counts once per open rather than on
  // every background-poll re-render of the open thread.
  const handleOpenConversation = (target: OfferConversationTarget) => {
    trackConversationOpened(leadId, target);
    setConversation(target);
  };
  const handleCloseConversation = () => setConversation(null);
  // The thread and card are read live by the snapshotted dealer's label so new messages and offer
  // changes appear while open.
  const conversationThread =
    conversation != null ? messagesByOrg.get(conversation.card.dealerLabel) : undefined;
  const liveConversationTarget = getLiveTarget(conversation, activeCards, inactiveCards);
  // The waiting-steps explainer is the live-listing baseline (no offers yet). Only once a real
  // offer exists — active or inactive — do we switch to the cards / empty-active treatment.
  const showOfferContent = offersEnabled && hasVisibleOffers;
  const showTierLanes = guidance != null && hasTierLanes(guidance.tier);

  // Summary row stats key off dealer-side pending offers (State A / B cards).
  const dealerSideCards = activeCards.filter((card) => card.driverCounter == null);
  const bestOffer = dealerSideCards[0]?.amount ?? null;
  const dealerSideCount = dealerSideCards.length;
  const driverCounterCount = activeCards.length - dealerSideCards.length;
  // The rule statement is about the driver's own counters, so it only applies when one is
  // outstanding (a State C / driver-side pending offer) — not merely when a dealer offer exists.
  const hasActiveDriverCounter = activeCards.some((card) => card.driverCounter != null);

  return (
    <OfferAnalyticsProvider
      enabled={offersEnabled}
      leadId={leadId}
      offers={offers ?? []}
      pendingOfferCount={dealerSideCount}
      pendingCounterCount={driverCounterCount}
    >
      <Flex
        as="section"
        aria-label="Offers"
        direction="column"
        h="100%"
        bg="white"
        borderWidth="1px"
        borderColor="grayMediumBackground"
        borderRadius="16px"
        overflow="hidden"
      >
        <HStack
          justify="space-between"
          align="center"
          spacing={3}
          flexWrap="nowrap"
          px={{ base: '16px', md: '24px' }}
          py={{ base: '14px', md: '18px' }}
          borderBottomWidth="1px"
          borderBottomColor="grayMediumBackground"
        >
          <HStack spacing={2} flexShrink={0}>
            {offersEnabled && <Icon as={FaStar} boxSize="18px" color="leaseEndYellow" />}
            <Text
              fontSize={{ base: '15px', md: '17px' }}
              fontWeight="700"
              color="leaseEndBlue"
              letterSpacing="0.02em"
            >
              Offers
            </Text>
          </HStack>

          {offersEnabled && (
            <OffersSummaryRow
              bestOffer={bestOffer}
              offerCount={dealerSideCount}
              listedAt={listedAt}
            />
          )}
        </HStack>

        {offersEnabled && hasActiveDriverCounter && (
          <Text
            px={{ base: '16px', md: '24px' }}
            pt={{ base: '12px', md: '16px' }}
            fontSize="12px"
            color="grayLight"
            lineHeight="1.5"
          >
            {RULE_STATEMENT}
          </Text>
        )}

        {!showOfferContent ? (
          <Center
            flex="1"
            px={{ base: '22px', md: '32px' }}
            pt={{ base: '32px', md: '48px' }}
            pb={{ base: '20px', md: '28px' }}
            minH={{ base: '180px', md: '380px' }}
          >
            <VStack spacing={{ base: 5, md: 7 }} w="100%">
              {showTierLanes ? (
                <TierLanes guidance={guidance} model={model} dealId={dealId} leadId={leadId} />
              ) : (
                <OffersWaitingSteps />
              )}
              <TrustStrip
                lienholderSlug={lienholderSlug}
                lienholderName={lienholderName}
                dealId={dealId}
                leadId={leadId}
                showDivider={!showTierLanes}
              />
            </VStack>
          </Center>
        ) : (
          <VStack
            align="stretch"
            spacing="12px"
            px={{ base: '16px', md: '22px' }}
            py={{ base: '14px', md: '18px' }}
          >
            {activeCards.length > 0 ? (
              activeCards.map((card) => (
                <OfferCard
                  key={card.offer.id}
                  card={card}
                  onAction={handleDashboardAction}
                  showConversationEntry={messagingEnabled}
                  thread={messagesByOrg.get(card.dealerLabel)}
                  onOpenConversation={() => handleOpenConversation({ kind: 'active', card })}
                  payoffAmount={payoffAmount}
                />
              ))
            ) : (
              <MarketplaceEmptyActive />
            )}

            {inactiveCards.length > 0 && (
              <>
                <InactiveOffersHeader />
                {inactiveCards.map((card) => (
                  <InactiveOfferCard
                    key={card.offer.id}
                    card={card}
                    showConversationEntry={messagingEnabled}
                    thread={messagesByOrg.get(card.dealerLabel)}
                    onOpenConversation={() => handleOpenConversation({ kind: 'inactive', card })}
                  />
                ))}
              </>
            )}
          </VStack>
        )}

        <OfferActionModals
          action={action}
          liveOffers={offers ?? []}
          externalId={externalId}
          dealId={dealId}
          payoffAmount={payoffAmount}
          payoffIsTrusted={payoffIsTrusted}
          onClose={handleCloseAction}
          onSuccess={handleActionSuccess}
        />

        {messagingEnabled && (
          <ConversationThreadDrawer
            target={liveConversationTarget}
            thread={conversationThread}
            externalId={externalId}
            leadId={leadId}
            onClose={handleCloseConversation}
            onAction={handleThreadAction}
            threadReanchorSignal={threadReanchorSignal}
          />
        )}
      </Flex>
    </OfferAnalyticsProvider>
  );
};

export default MarketplaceOffers;
